import React, { ReactNode, useLayoutEffect, useMemo } from 'react';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { Helmet } from 'react-helmet-async';

import { generateBorderSizeValues } from 'src/theme/borders';
import {
  alert,
  black,
  dark,
  light,
  link,
  success,
  warning,
  white,
  getColorShades,
} from 'src/theme/colors';
import { useCompany } from './Company.store';
import { cacheBrandColor, clearCachedBrandColor } from './brandColorCache';

// The new (shadcn / Untitled UI) portal renders in Geist by default. Keep it as
// the fallback after any tenant-selected font so weights the brand font lacks
// still resolve sensibly.
const GEIST_FALLBACK = `'Geist Sans', 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

// Runtime-overridable font tokens: --font-sans drives the shadcn `.wh-ui` scope
// + Radix portals (tailwind.css); --font-body/--font-display drive the Untitled
// UI layer (theme.css). Overriding all three applies a brand font across the
// whole new portal.
const PORTAL_FONT_VARS = ['--font-sans', '--font-body', '--font-display'];

// Named families already loaded by index.html / the Geist CDN — selecting one
// overrides Geist but needs no extra Google Fonts request.
const BUNDLED_FONTS = new Set([
  'Poppins',
  'IBM Plex Sans',
  'Geist',
  'Geist Sans',
]);

export const ThemeStoreProvider = ({ children }: { children: ReactNode }) => {
  const { data: company } = useCompany();

  // Drive the loading-spinner arc from the company's "Main color (Dark)"
  // (primary_color) so the in-app spinner is brand-coloured per tenant. Falls
  // back to the --spinner-color default (navy) in tokens.css when unset. The
  // index.html boot splash can't use this — it paints before the company is
  // fetched — so it stays the static navy default.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (company.primary_color) {
      root.style.setProperty('--spinner-color', company.primary_color);
      // Persist for the next reload so the spinner is brand-coloured from the
      // first frame (read synchronously in src/index.tsx).
      cacheBrandColor(company.primary_color);
    } else {
      root.style.removeProperty('--spinner-color');
      // Drop the persisted color too, so a tenant that unsets primary_color
      // doesn't keep repainting the stale brand color on every reload.
      clearCachedBrandColor();
    }
  }, [company.primary_color]);

  // Resolve the tenant's "Main font" for the NEW portal surfaces. An uploaded
  // font wins (its @font-face is injected below); otherwise the named dropdown
  // selection. Poppins (the backend default) and an unset value both map to the
  // portal's built-in Geist, so we leave the tokens untouched in that case.
  const portalFontFamily = company.main_portal_font
    ? 'CustomPrimaryFont'
    : company.main_portal_font_family &&
        company.main_portal_font_family !== 'Poppins'
      ? company.main_portal_font_family
      : null;

  // A named (non-uploaded) family that isn't already bundled must be fetched
  // from Google Fonts to actually render; otherwise the stack falls back to
  // Geist. Best-effort: a name not hosted on Google Fonts simply 404s.
  //
  // Derived from the already-resolved portalFontFamily so "which font is active"
  // has a single source of truth (it already excludes uploaded fonts as
  // 'CustomPrimaryFont' and maps Poppins/unset to null). encodeURIComponent
  // keeps metacharacters in the tenant-supplied name (&, #, =, …) from
  // corrupting the query string; spaces use '+' per Google's css2 format.
  const googleFontHref =
    portalFontFamily &&
    portalFontFamily !== 'CustomPrimaryFont' &&
    !BUNDLED_FONTS.has(portalFontFamily)
      ? `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
          portalFontFamily
        ).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`
      : null;

  // Drive the new portal's base font from the resolved brand font, mirroring the
  // --spinner-color pattern above. Legacy antd pages set their body font
  // directly in index.css and are unaffected.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (portalFontFamily) {
      const stack = `'${portalFontFamily}', ${GEIST_FALLBACK}`;
      PORTAL_FONT_VARS.forEach((v) => root.style.setProperty(v, stack));
    } else {
      PORTAL_FONT_VARS.forEach((v) => root.style.removeProperty(v));
    }
  }, [portalFontFamily]);

  const theme = useMemo<DefaultTheme>(
    () => ({
      colors: {
        ...getColorShades('dark', company.primary_color || dark),
        ...getColorShades('light', company.secondary_color || light),
        ...getColorShades('white', white),
        ...getColorShades('black', black),
        ...getColorShades('alert', alert),
        ...getColorShades('success', success),
        ...getColorShades('warning', warning),
        ...getColorShades('link', link),
      },
      borders: generateBorderSizeValues(company.border_radius || 'medium'),
      fonts: {
        primary: company.main_portal_font
          ? 'CustomPrimaryFont'
          : company.main_portal_font_family || 'Poppins',
        secondary: company.secondary_portal_font
          ? 'CustomSecondaryFont'
          : company.secondary_portal_font_family || 'Poppins',
      },
    }),
    [company]
  );

  return (
    <>
      <Helmet
        link={
          googleFontHref
            ? [{ rel: 'stylesheet', href: googleFontHref }]
            : undefined
        }
        style={[
          {
            cssText: `${
              company.main_portal_font
                ? `
                  @font-face {
                    font-family: 'CustomPrimaryFont';
                    src: url("${company.main_portal_font}");
                  }
                `
                : ''
            }\n${
              company.secondary_portal_font
                ? `
                  @font-face {
                    font-family: 'CustomSecondaryFont';
                    src: url("${company.secondary_portal_font}");
                  }
                `
                : ''
            }`,
          },
        ]}
      />
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </>
  );
};
